<!--
+------------------------------------------------------------------+
| Author: Jeffrey Thümann                                          |
| Component: EventMap                                              |
| Section: components                                              |
| Purpose: Map view plotting events with marker interaction.       |
+------------------------------------------------------------------+
-->
<template>
    <div class="EventMap">
        <l-map v-if="events.length > 0" :zoom="zoom" :center="mapCenter" style="height: 100%;" ref="leafletMap"
            @ready="onMapReady">
            <l-tile-layer :url="tileLayerUrl" :attribution="tileLayerAttribution" />
            <l-polyline v-for="(segment, index) in chronologicalSegments" :key="`segment-${index}`"
                :lat-lngs="segment.latLngs" :color="lineColor" :dash-array="lineDashArray" :weight="segment.weight">
                <l-tooltip v-if="segment.count > 1" :options="{
                    permanent: true,
                    direction: 'center',
                    className: 'segment-count-label',
                    opacity: 1
                }" :interactive="false">
                    ×{{ segment.count }}
                </l-tooltip>
            </l-polyline>
            <l-marker v-for="(group, index) in events" :key="index" :lat-lng="[group.latitude, group.longitude]"
                :icon="customIcon">
                <l-popup>
                    <a :href="`/plaatsen/${group.events[0].ID}`" class="popup-text">{{ group.events[0].place }}</a>
                    <ul>
                        <li class="popup-list-item" v-for="(event, idx) in group.events" :key="idx">
                            {{ event.type }}
                        </li>
                    </ul>
                </l-popup>
            </l-marker>
        </l-map>
    </div>
</template>

<script>
import { LMap, LTileLayer, LMarker, LPopup, LPolyline, LTooltip } from "@vue-leaflet/vue-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import "leaflet.fullscreen";
import "leaflet.fullscreen/Control.FullScreen.css";
import { getEventLocations } from "@/api/personService";

export default {
    name: "EventMap",
    emits: ["empty-state-change"],
    components: {
        LMap,
        LTileLayer,
        LMarker,
        LPopup,
        LPolyline,
        LTooltip,
    },
    props: {
        personID: {
            type: String,
            required: true,
        },
    },
    data() {
        return {
            events: [],
            flatEvents: [],
            eventLocationsRequestId: 0,
            mapCenter: [15.0, 48.0],
            zoom: 3,
            mapInstance: null,
            tileLayerUrl: "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",
            tileLayerAttribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
            lineColor: "#2f2f2f",
            lineDashArray: "6 8",
            lineWeight: 2,
            customIcon: L.icon({
                iconUrl: require("@/assets/icons/pin.svg"),
                iconSize: [32, 32],
                iconAnchor: [16, 32],
            }),
        };
    },
    watch: {
        personID: {
            immediate: true,
            handler(personID) {
                this.loadEventLocations(personID);
            }
        }
    },
    computed: {
        eventsWithCoordinates() {
            return this.events.filter(
                event =>
                    event.latitude &&
                    event.longitude &&
                    !isNaN(event.latitude) &&
                    !isNaN(event.longitude)
            );
        },
        chronologicalSegments() {
            const sortable = this.flatEvents
                .filter(event =>
                    event.latitude &&
                    event.longitude &&
                    !isNaN(event.latitude) &&
                    !isNaN(event.longitude)
                )
                .map(event => ({
                    ...event,
                    sortKey: this.getEventSortKey(event)
                }))
                .sort((a, b) => {
                    if (a.sortKey === b.sortKey) {
                        return (a.originalIndex || 0) - (b.originalIndex || 0);
                    }
                    return a.sortKey - b.sortKey;
                });

            const points = sortable.map(event => [Number(event.latitude), Number(event.longitude)]);
            return this.buildChronologicalSegments(points);
        }
    },
    methods: {
        localizedEventType(type, childName = '') {
            const supportedTypes = new Set([
                'birth', 'death', 'baptism', 'burial', 'marriage',
                'EDUC', 'ADDR', 'IMMI', 'CHRA', 'NATU', 'childBirth', 'childBaptism'
            ]);
            const key = supportedTypes.has(type) ? type : 'other';
            return this.$t(`eventMap.eventTypes.${key}`, { name: childName });
        },
        buildChronologicalSegments(points) {
            const segments = new Map();

            for (let index = 1; index < points.length; index += 1) {
                const from = points[index - 1];
                const to = points[index];

                if (!from || !to || (from[0] === to[0] && from[1] === to[1])) {
                    continue;
                }

                const key = this.getSegmentKey(from, to);
                if (!segments.has(key)) {
                    segments.set(key, {
                        latLngs: [from, to],
                        count: 0
                    });
                }

                segments.get(key).count += 1;
            }

            return Array.from(segments.values()).map(segment => ({
                ...segment,
                weight: this.getSegmentWeight(segment.count)
            }));
        },
        getSegmentKey(from, to) {
            const fromKey = `${from[0]},${from[1]}`;
            const toKey = `${to[0]},${to[1]}`;
            return fromKey < toKey ? `${fromKey}<->${toKey}` : `${toKey}<->${fromKey}`;
        },
        getSegmentWeight(count) {
            const base = this.lineWeight;
            const extra = Math.min(6, Math.max(0, count - 1));
            return base + extra;
        },
        getEventSortKey(event) {
            const dateCandidates = [event.date, event.dateText, event.eventdate, event.eventdatetr]
                .filter(Boolean);

            for (const candidate of dateCandidates) {
                const parsed = this.parseDateToTimestamp(candidate);
                if (parsed !== null) return parsed;
            }

            const fallbackBase = Number.MAX_SAFE_INTEGER - 1000000;
            return fallbackBase + (event.originalIndex || 0);
        },
        parseDateToTimestamp(dateString) {
            if (!dateString || typeof dateString !== "string") return null;

            const trimmed = dateString.trim();
            if (!trimmed || trimmed === "0000-00-00") return null;

            const normalized = trimmed
                .replace(/^(?:[~±≈]|c\.?|ca\.?|circa)\s*/i, "")
                .replace(/\?/g, "")
                .trim();

            if (!normalized) return null;

            if (/^\d{4}$/.test(normalized)) {
                const year = Number(normalized);
                return Number.isFinite(year) ? Date.UTC(year, 0, 1) : null;
            }

            if (/^\d{4}-\d{2}-\d{2}$/.test(normalized)) {
                const [year, month, day] = normalized.split("-").map(Number);
                if (!year) return null;
                const safeMonth = month > 0 ? month - 1 : 0;
                const safeDay = day > 0 ? day : 1;
                return Date.UTC(year, safeMonth, safeDay);
            }

            const monthMap = {
                jan: 0,
                january: 0,
                janv: 0,
                feb: 1,
                februari: 1,
                february: 1,
                mrt: 2,
                maart: 2,
                mar: 2,
                march: 2,
                apr: 3,
                april: 3,
                mei: 4,
                may: 4,
                jun: 5,
                juni: 5,
                june: 5,
                jul: 6,
                juli: 6,
                july: 6,
                aug: 7,
                augustus: 7,
                august: 7,
                sep: 8,
                sept: 8,
                september: 8,
                okt: 9,
                october: 9,
                oktober: 9,
                nov: 10,
                november: 10,
                dec: 11,
                december: 11
            };

            const dayMonthYearMatch = normalized.match(/^(\d{1,2})\s+([A-Za-z.]+)\s+(\d{4})$/);
            if (dayMonthYearMatch) {
                const day = Number(dayMonthYearMatch[1]);
                const monthKey = dayMonthYearMatch[2].toLowerCase().replace(".", "");
                const year = Number(dayMonthYearMatch[3]);
                const month = monthMap[monthKey];
                if (Number.isFinite(year) && Number.isFinite(month) && Number.isFinite(day)) {
                    return Date.UTC(year, month, day);
                }
            }

            const monthYearMatch = normalized.match(/^([A-Za-z.]+)\s+(\d{4})$/);
            if (monthYearMatch) {
                const monthKey = monthYearMatch[1].toLowerCase().replace(".", "");
                const year = Number(monthYearMatch[2]);
                const month = monthMap[monthKey];
                if (Number.isFinite(year) && Number.isFinite(month)) {
                    return Date.UTC(year, month, 1);
                }
            }

            const parsed = Date.parse(normalized);
            return Number.isNaN(parsed) ? null : parsed;
        },
        onMapReady(map) {
            this.mapInstance = map;

            // Add fullscreen control
            L.control.fullscreen({
                position: "bottomleft", // Position of the button
                title: {
                    false: "View Fullscreen", // Title when the map is not fullscreen
                    true: "Exit Fullscreen", // Title when the map is fullscreen
                },
                content: null, // Custom content inside the button (optional)
            }).addTo(map);

            if (this.eventsWithCoordinates.length === 0) {
                this.mapInstance.setView(this.mapCenter, this.zoom);
            } else {
                setTimeout(() => {
                    this.adjustMapBounds(this.eventsWithCoordinates);
                }, 200);
            }

            this.$nextTick(() => {
                this.mapInstance.invalidateSize();
            });
        },
        adjustMapBounds(events) {
            if (!events || events.length === 0) return;

            if (events.length === 1) {
                // If there's only one event, set the map view to the event's coordinates with a default zoom level
                this.mapInstance.setView([events[0].latitude, events[0].longitude], 10); // Adjust zoom level as needed
            } else {
                // Otherwise, fit the map to the bounds of all events
                const bounds = L.latLngBounds(events.map(event => [event.latitude, event.longitude]));
                this.mapInstance.fitBounds(bounds, { padding: [48, 48] });
            }
        },
        async loadEventLocations(personID) {
            const requestId = this.eventLocationsRequestId + 1;
            this.eventLocationsRequestId = requestId;
            this.events = [];
            this.flatEvents = [];
            this.mapInstance = null;

            if (!personID) {
                this.$emit("empty-state-change", true);
                return;
            }

            // Keep the map mounted while the locations for the new person load.
            this.$emit("empty-state-change", false);

            try {
                const { personEventLocations, eventLocations, childrenBirthLocations, childrenBaptismLocations } = await getEventLocations(personID);

                if (requestId !== this.eventLocationsRequestId || personID !== this.personID) {
                    return;
                }

                const allEvents = [
                    ...(personEventLocations || []),
                    ...(eventLocations || []),
                    ...(childrenBirthLocations || []),
                    ...(childrenBaptismLocations || []),
                ].map(location => ({
                    ...location,
                    type: this.localizedEventType(location.type, location.childName)
                }));

                const eventsWithIndex = allEvents.map((event, index) => ({
                    ...event,
                    originalIndex: index
                }));

                const validEvents = eventsWithIndex.filter(
                    event =>
                        event.latitude !== null &&
                        event.latitude !== undefined &&
                        event.longitude !== null &&
                        event.longitude !== undefined &&
                        !isNaN(Number(event.latitude)) &&
                        !isNaN(Number(event.longitude))
                );

                if (validEvents.length === 0) {
                    console.warn("No valid event locations found for personID:", personID);
                }

                const groupedEvents = validEvents.reduce((acc, event) => {
                    const key = `${event.latitude}-${event.longitude}`;
                    if (!acc[key]) {
                        acc[key] = {
                            latitude: parseFloat(event.latitude),
                            longitude: parseFloat(event.longitude),
                            events: [],
                        };
                    }
                    acc[key].events.push(event);
                    return acc;
                }, {});

                this.events = Object.values(groupedEvents);
                this.flatEvents = validEvents;
                this.$emit("empty-state-change", validEvents.length === 0);

                console.table(this.flatEvents.map(event => ({
                    type: event.type,
                    date: event.date || event.dateText || event.eventdate || event.eventdatetr || null,
                    lat: event.latitude,
                    lng: event.longitude
                })));

                const chronologicalPreview = this.flatEvents
                    .filter(event =>
                        event.latitude &&
                        event.longitude &&
                        !isNaN(event.latitude) &&
                        !isNaN(event.longitude)
                    )
                    .map(event => ({
                        ...event,
                        sortKey: this.getEventSortKey(event)
                    }))
                    .sort((a, b) => {
                        if (a.sortKey === b.sortKey) {
                            return (a.originalIndex || 0) - (b.originalIndex || 0);
                        }
                        return a.sortKey - b.sortKey;
                    })
                    .map(event => ({
                        type: event.type,
                        sortKey: event.sortKey,
                        lat: event.latitude,
                        lng: event.longitude
                    }));

                console.table(chronologicalPreview);
            } catch (error) {
                if (requestId !== this.eventLocationsRequestId || personID !== this.personID) {
                    return;
                }

                console.error(
                    "Failed to fetch event locations:",
                    error.response ? error.response.data : error.message
                );
                this.$emit("empty-state-change", true);
            }
        }
    },
    beforeUnmount() {
        this.eventLocationsRequestId += 1;
    }
};
</script>

<style scoped>
.EventMap {
    height: 500px;
    width: 100%;
    max-width: calc(50% + 48px);
    position: relative;
    z-index: 1;
}

.popup-list-item {
    font-size: 12px;
}

.segment-count-label {
    background: rgba(255, 255, 255, 0.9);
    border: 1px solid rgba(0, 0, 0, 0.2);
    border-radius: 10px;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.2);
    color: #222;
    font-size: 11px;
    font-weight: 600;
    padding: 2px 6px;
}

.leaflet-container:fullscreen {
    height: 100% !important;
    width: 100% !important;
}

.leaflet-container:-webkit-full-screen {
    height: 100% !important;
    width: 100% !important;
}

.leaflet-container:-ms-fullscreen {
    height: 100% !important;
    width: 100% !important;
}

.leaflet-container:fullscreen a.leaflet-fullscreen-button {
    background-color: #fff;
}

@media (max-width: 860px) {
    .EventMap {
        max-width: 100%;
        height: 32vh;
    }
}
</style>
